import { useMemo, useState } from "react";

export type SortDir = "asc" | "desc";
export interface SortState<K extends string> { key: K; dir: SortDir }
/** Row → sortable value. null/undefined/"" (all rendered as "—") sink to the
 *  bottom in BOTH directions so real values never hide below a dash block. */
export type SortAccessor<T> = (row: T) => string | number | null | undefined;

/** Client-side header sorting for the shared .table grammar (ADR 151).
 *  Per-column cycle: asc → desc → natural (server) order. Strings compare
 *  pt-BR locale-aware and numeric-smart ("A2" < "A10"), which is what makes
 *  address columns like "Jx01-02B" sort in rack order.
 *  Pass the accessor map as a module-level constant — an inline literal would
 *  re-sort on every render. */
export function useTableSort<T, K extends string>(
  rows: readonly T[],
  accessors: Record<K, SortAccessor<T>>,
) {
  const [sort, setSort] = useState<SortState<K> | null>(null);

  const sorted = useMemo(() => {
    if (!sort) return [...rows];
    const get = accessors[sort.key];
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((x, y) => {
      const a = get(x);
      const b = get(y);
      const aNull = a == null || a === "";
      const bNull = b == null || b === "";
      if (aNull || bNull) return aNull && bNull ? 0 : aNull ? 1 : -1;
      const r = typeof a === "number" && typeof b === "number"
        ? a - b
        : String(a).localeCompare(String(b), "pt-BR", { numeric: true, sensitivity: "base" });
      return r * dir;
    });
  }, [rows, sort, accessors]);

  const toggle = (key: K) =>
    setSort((s) =>
      s?.key !== key ? { key, dir: "asc" }
        : s.dir === "asc" ? { key, dir: "desc" }
          : null,
    );

  return { rows: sorted, sort, toggle };
}
